import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  IconButton,
  Theme,
  CSSObject,
  CssBaseline,
  Typography,
  styled,
  useTheme,
  ListItemButton,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HomeIcon from "@mui/icons-material/Home";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MuiDrawer from "@mui/material/Drawer";
import BuildIcon from "@mui/icons-material/Build";
import LiquorIcon from "@mui/icons-material/Liquor";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import Discount from "@mui/icons-material/Discount";

import { usePhrases } from "../../context/PhrasesContext";
import { Navbar } from "./Navbar";

export const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

export const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export const Sidebar = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const { getPhrase } = usePhrases();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Navbar handleDrawerOpen={handleDrawerOpen} open={open} />
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {[
            { text: getPhrase("SIDEBAR.HOME"), icon: <HomeIcon />, link: "/" },
            {
              text: getPhrase("SIDEBAR.CUSTOMERS"),
              icon: <PeopleIcon />,
              link: "/customers",
            },
            {
              text: getPhrase("SIDEBAR.ORDERS"),
              icon: <AssignmentIcon />,
              link: "/orders",
            },
            {
              text: getPhrase("SIDEBAR.PRODUCTS"),
              icon: <LiquorIcon />,
              link: "/products",
            },
            {
              text: getPhrase("SIDEBAR.EQUIPMENT"),
              icon: <BuildIcon />,
              link: "/equipments",
            },
            {
              text: getPhrase("SIDEBAR.DISCOUNT"),
              icon: <Discount />,
              link: "/discounts",
            },
            {
              text: getPhrase("SIDEBAR.ANALYTICS"),
              icon: <AnalyticsIcon />,
              link: "/analytics",
            },
            {
              text: getPhrase("SIDEBAR.USERS"),
              icon: <AccountCircleIcon />,
              link: "/users",
            },
          ].map((item) => (
            <ListItem
              component={NavLink}
              to={item.link}
              key={item.text}
              disablePadding
              sx={{ display: "block" }}
            >
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText sx={{ opacity: open ? 1 : 0 }}>
                  <Typography color={"black"} variant="body1">
                    {item.text}
                  </Typography>
                </ListItemText>
              </ListItemButton>
            </ListItem>
          ))}
          {/* <Divider /> */}
        </List>
      </Drawer>
      <Box pb={5} mb={5} component="main" sx={{ flexGrow: 1 }}>
        <DrawerHeader />
        {children}
      </Box>
    </Box>
  );
};
